/**
 * AutoRefreshManager - Environment-aware refresh for feature flags.
 *
 * Production can poll while visible. Other environments can refresh only
 * when the user returns or requests it.
 */

import type { AutoRefreshConfig } from '#~/posthog/types.js';
import { FeatureFlagEventDispatcher } from '#~/posthog/events.js';
import type { PostHogEnvironment } from '#~/config/types.js';

const FOREGROUND_REFRESH_DEDUPE_MS = 1_000;

/**
 * Default configuration for auto-refresh behavior.
 */
const DEFAULT_CONFIG: AutoRefreshConfig = {
  activeInterval: 30_000,
  inactiveInterval: 120_000,
  hiddenTabInterval: 300_000, // 5 minutes when tab is hidden
  inactivityThreshold: 60_000, // 1 minute to consider user inactive
  enabled: true,
  pollingEnabled: true,
  pollWhenHidden: false,
  refreshOnFocus: true
};

/** Select flag freshness from the PostHog project receiving the requests. */
export const resolveFeatureFlagRefreshConfig = (
  posthogEnv: PostHogEnvironment
): AutoRefreshConfig => {
  const foregroundRefreshEnabled = posthogEnv !== 'off' && posthogEnv !== 'unknown';

  return {
    ...DEFAULT_CONFIG,
    enabled: foregroundRefreshEnabled,
    pollingEnabled: posthogEnv === 'prod',
    refreshOnFocus: foregroundRefreshEnabled
  };
};

/**
 * Manages polling and foreground refreshes for feature flags.
 */
export class AutoRefreshManager {
  private refreshTimeout: number | null = null;
  private inactivityCheckInterval: number | null = null;
  private isUserActive: boolean = true;
  private lastActivity: number = Date.now();
  private config: AutoRefreshConfig;
  private isDestroyed: boolean = false;
  private refreshCallback: (() => Promise<void>) | null = null;
  private refreshInFlight: Promise<void> | null = null;
  private lastRefreshAt: number | null = null;
  private markActiveHandler: (() => void) | null = null;
  private visibilityChangeHandler: (() => void) | null = null;
  private focusHandler: (() => void) | null = null;
  private interactionEvents: string[] = [];
  private interactionEventOptions: AddEventListenerOptions = { passive: true };

  /**
   * Create a new AutoRefreshManager instance.
   *
   * @param refreshCallback - Function to call when refresh is needed
   * @param config - Configuration options (optional)
   */
  constructor(refreshCallback: () => Promise<void>, config: Partial<AutoRefreshConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.refreshCallback = refreshCallback;

    if (this.config.enabled) {
      this.initialize();
    }
  }

  /**
   * Initialize the auto-refresh system.
   */
  private initialize(): void {
    this.setupActivityDetection();
    this.scheduleNextRefresh();
    this.log('>>>AutoRefreshManager initialized', {
      config: this.config,
      isActive: this.isUserActive
    });
  }

  /**
   * Set up activity detection listeners.
   */
  private setupActivityDetection(): void {
    // Clean up any existing listeners first
    this.cleanupEventListeners();

    const markActive = (): void => {
      const wasInactive = !this.isUserActive;
      this.lastActivity = Date.now();
      this.isUserActive = true;

      // If we became active, reschedule for faster refresh
      if (wasInactive && this.config.enabled) {
        this.log('>>>User became active, rescheduling refresh');
        this.reschedule();
      }
    };

    // Store handler reference for cleanup
    this.markActiveHandler = markActive;

    if (this.config.pollingEnabled) {
      this.interactionEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
      this.interactionEvents.forEach((event) => {
        document.addEventListener(event, this.markActiveHandler!, this.interactionEventOptions);
      });
    }

    // Page visibility changes
    const visibilityChangeHandler = (): void => {
      if (document.hidden) {
        this.reschedule();
        return;
      }

      markActive();
      if (this.config.refreshOnFocus) {
        void this.triggerForegroundRefresh('visibility change');
      } else {
        this.reschedule();
      }
    };

    // Store handler reference for cleanup
    this.visibilityChangeHandler = visibilityChangeHandler;
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);

    this.focusHandler = (): void => {
      if (!document.hidden && this.config.refreshOnFocus) {
        void this.triggerForegroundRefresh('window focus');
      }
    };
    window.addEventListener('focus', this.focusHandler);

    // Periodic inactivity check
    if (this.inactivityCheckInterval !== null) {
      clearInterval(this.inactivityCheckInterval);
      this.inactivityCheckInterval = null;
    }

    if (!this.config.pollingEnabled) {
      return;
    }

    this.inactivityCheckInterval = window.setInterval(() => {
      if (this.isDestroyed) {
        if (this.inactivityCheckInterval !== null) {
          clearInterval(this.inactivityCheckInterval);
          this.inactivityCheckInterval = null;
        }
        return;
      }

      const now = Date.now();
      const wasActive = this.isUserActive;

      if (now - this.lastActivity > this.config.inactivityThreshold) {
        this.isUserActive = false;
        // If we became inactive, reschedule for slower refresh
        if (wasActive && this.config.enabled) {
          this.log('>>>User became inactive, rescheduling refresh');
          this.reschedule();
        }
      }
    }, 30_000);
  }

  /**
   * Get the current refresh interval based on activity and visibility.
   */
  private getCurrentInterval(): number | null {
    if (!this.config.pollingEnabled) {
      return null;
    }

    if (document.hidden) {
      return this.config.pollWhenHidden ? this.config.hiddenTabInterval : null;
    }

    return this.isUserActive ? this.config.activeInterval : this.config.inactiveInterval;
  }

  /**
   * Schedule the next refresh.
   */
  private scheduleNextRefresh(): void {
    if (this.refreshTimeout !== null) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }

    if (this.isDestroyed || !this.config.enabled) {
      return;
    }

    const interval = this.getCurrentInterval();
    if (interval === null) {
      FeatureFlagEventDispatcher.dispatchAutoRefreshStateChanged(false, 0);
      return;
    }

    this.refreshTimeout = window.setTimeout(() => {
      this.performRefresh();
    }, interval);

    this.log(`>>>Next refresh scheduled in ${interval}ms`, {
      isActive: this.isUserActive,
      isHidden: document.hidden,
      interval
    });

    FeatureFlagEventDispatcher.dispatchAutoRefreshStateChanged(true, interval);
  }

  /**
   * Perform the actual refresh.
   */
  private async performRefresh(): Promise<void> {
    if (this.isDestroyed || !this.refreshCallback) {
      return;
    }

    try {
      this.log('>>>Performing auto-refresh');
      await this.runRefresh();
      this.log('>>>Auto-refresh completed successfully');
    } catch (error) {
      this.logError('>>>Auto-refresh failed', error);
    } finally {
      // Schedule next refresh
      this.scheduleNextRefresh();
    }
  }

  /** Collapse focus and visibility events into one paid flag request. */
  private async triggerForegroundRefresh(source: string): Promise<void> {
    if (
      this.lastRefreshAt !== null &&
      Date.now() - this.lastRefreshAt < FOREGROUND_REFRESH_DEDUPE_MS
    ) {
      this.reschedule();
      return;
    }

    try {
      this.log(`>>>Refreshing after ${source}`);
      await this.runRefresh();
    } catch (error) {
      this.logError(`>>>Failed to refresh after ${source}`, error);
    } finally {
      this.reschedule();
    }
  }

  /** Share one request when a timer and foreground event arrive together. */
  private async runRefresh(): Promise<void> {
    if (!this.refreshCallback) {
      return;
    }

    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    // The async boundary also converts a synchronous callback failure into the
    // same rejected promise path as a network failure.
    const refresh = (async () => {
      await this.refreshCallback?.();
      this.lastRefreshAt = Date.now();
    })();
    this.refreshInFlight = refresh;

    try {
      await refresh;
    } finally {
      if (this.refreshInFlight === refresh) {
        this.refreshInFlight = null;
      }
    }
  }

  /**
   * Reschedule the next refresh (called when activity state changes).
   */
  private reschedule(): void {
    if (this.isDestroyed || !this.config.enabled) {
      return;
    }

    this.log('>>>Rescheduling refresh due to activity change');
    this.scheduleNextRefresh();
  }

  /**
   * Enable auto-refresh.
   */
  enable(): void {
    if (this.isDestroyed) {
      this.logError('>>>Cannot enable destroyed AutoRefreshManager');
      return;
    }

    if (!this.config.enabled) {
      this.config.enabled = true;
      this.log('>>>Auto-refresh enabled');
      this.initialize();
    }
  }

  /**
   * Disable auto-refresh.
   */
  disable(): void {
    this.config.enabled = false;

    if (this.refreshTimeout !== null) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }

    if (this.inactivityCheckInterval !== null) {
      clearInterval(this.inactivityCheckInterval);
      this.inactivityCheckInterval = null;
    }

    this.cleanupEventListeners();

    this.log('>>>Auto-refresh disabled');
    FeatureFlagEventDispatcher.dispatchAutoRefreshStateChanged(false, 0);
  }

  /**
   * Update the configuration.
   */
  updateConfig(newConfig: Partial<AutoRefreshConfig>): void {
    if (this.isDestroyed) {
      this.logError('>>>Cannot update config of destroyed AutoRefreshManager');
      return;
    }

    const oldEnabled = this.config.enabled;
    this.config = { ...this.config, ...newConfig };

    this.log('>>>Configuration updated', {
      oldConfig: { enabled: oldEnabled },
      newConfig: this.config
    });

    // Handle enable/disable state changes
    if (!oldEnabled && this.config.enabled) {
      this.initialize();
    } else if (oldEnabled && !this.config.enabled) {
      this.disable();
    } else if (this.config.enabled) {
      this.setupActivityDetection();
      this.scheduleNextRefresh();
    }
  }

  /**
   * Get current configuration.
   */
  getConfig(): Readonly<AutoRefreshConfig> {
    return { ...this.config };
  }

  /**
   * Get current status.
   */
  getStatus(): {
    enabled: boolean;
    isUserActive: boolean;
    lastActivity: number;
    currentInterval: number | null;
    nextRefreshIn: number;
  } {
    const currentInterval = this.getCurrentInterval();
    const nextRefreshIn =
      this.refreshTimeout && currentInterval !== null
        ? Math.max(0, currentInterval - (Date.now() - this.lastActivity))
        : 0;

    return {
      enabled: this.config.enabled,
      isUserActive: this.isUserActive,
      lastActivity: this.lastActivity,
      currentInterval,
      nextRefreshIn
    };
  }

  /**
   * Trigger an immediate refresh.
   */
  async triggerRefresh(): Promise<void> {
    if (this.isDestroyed || !this.refreshCallback) {
      return;
    }

    this.log('>>>Manual refresh triggered');

    try {
      await this.runRefresh();
      this.log('>>>Manual refresh completed');
    } catch (error) {
      this.logError('>>>Manual refresh failed', error);
      throw error;
    }

    // Reschedule next auto-refresh
    if (this.config.enabled) {
      this.scheduleNextRefresh();
    }
  }

  private cleanupEventListeners(): void {
    if (this.markActiveHandler && this.interactionEvents.length > 0) {
      this.interactionEvents.forEach((event) => {
        document.removeEventListener(event, this.markActiveHandler!, this.interactionEventOptions);
      });
      this.interactionEvents = [];
    }

    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      this.visibilityChangeHandler = null;
    }

    if (this.focusHandler) {
      window.removeEventListener('focus', this.focusHandler);
      this.focusHandler = null;
    }

    this.markActiveHandler = null;
  }

  /**
   * Clean up and destroy the manager.
   */
  destroy(): void {
    if (this.isDestroyed) {
      return;
    }

    this.log('>>>Destroying AutoRefreshManager');

    this.isDestroyed = true;
    this.disable();

    this.refreshCallback = null;
  }

  /**
   * Check if the manager is destroyed.
   */
  isDestroyed_(): boolean {
    return this.isDestroyed;
  }

  /**
   * Log a debug message (only in debug mode).
   */
  private log(message: string, data?: any): void {
    // Only log in development or when debugging is enabled
    if (process.env.NODE_ENV === 'development') {
      const timestamp = new Date().toISOString();
      const prefix = `%c[AutoRefreshManager ${timestamp}]`;
      const style = 'color: #9f7aea; font-weight: bold;';

      if (data !== undefined) {
        console.log(prefix, style, message, data);
      } else {
        console.log(prefix, style, message);
      }
    }
  }

  /**
   * Log an error message.
   */
  private logError(message: string, error?: any): void {
    const timestamp = new Date().toISOString();
    const prefix = `%c[AutoRefreshManager ${timestamp}]`;
    const style = 'color: #e53e3e; font-weight: bold;';

    if (error !== undefined) {
      console.error(prefix, style, message, error);
    } else {
      console.error(prefix, style, message);
    }
  }
}
